import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuthStore } from '../../stores/authStore';
import { companyApi } from '../../lib/api';
import { Company } from '@dutchinvoice/types';
import { FloatingLabelInput } from './FloatingLabelInput';
import { DeleteConfirmDialog } from '../DeleteConfirmDialog';
import { Trash2 } from 'lucide-react';

interface CompanyAutosuggestProps {
  value: string;
  onChange: (value: string) => void;
  onCompanySelect: (company: Company) => void;
  onCreateCompany: (name: string) => Promise<Company>;
  onClearCompany?: () => void;
  companyId?: string;
  placeholder?: string;
  label?: string;
  isPlaceholder?: boolean;
  autoFocus?: boolean;
  onCustomFocus?: (name?: string) => void;
  onCustomBlur?: (name?: string) => void;
}

export const CompanyAutosuggest: React.FC<CompanyAutosuggestProps> = ({
  value,
  onChange,
  onCompanySelect,
  onCreateCompany,
  onClearCompany,
  companyId,
  placeholder = '',
  label = 'Company',
  isPlaceholder = false,
  autoFocus = false,
  onCustomFocus,
  onCustomBlur,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load initial companies when the component mounts or when the dropdown opens
  useEffect(() => {
    if (isAuthenticated && isOpen && !companyId) {
      fetchCompanies();
    }
  }, [isAuthenticated, isOpen, companyId]);

  // Search for companies when the value changes
  useEffect(() => {
    if (isAuthenticated && isOpen && !companyId) {
      const delayDebounceFn = setTimeout(() => {
        fetchCompanies(value);
      }, 300);
      
      return () => clearTimeout(delayDebounceFn);
    }
  }, [value, isAuthenticated, isOpen, companyId]);

  // Auto-focus the input when specified
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchCompanies = async (query: string = '') => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    try {
      const response = await companyApi.search(query);
      setCompanies(response.companies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputFocus = () => {
    if (!companyId) {
      setIsOpen(true);
    }
    onCustomFocus?.('company');
  };

  const handleInputBlur = () => {
    // Don't close the dropdown immediately to allow clicking on options
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        onCustomBlur?.('company');
      }
    }, 200);
  };

  const handleCompanySelect = (company: Company) => {
    onCompanySelect(company);
    setIsOpen(false);
  };

  const handleClearCompany = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClearCompany) {
      onClearCompany();
      // Clear the input value
      onChange('');
      // Focus the input after clearing
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          setIsOpen(true);
        }
      }, 0);
    }
  };

  const handleCreateCompany = async () => {
    if (!value.trim()) return;
    
    setIsCreating(true);
    try {
      await onCreateCompany(value);
      setCreateSuccess(true);
      
      // Reset after animation
      setTimeout(() => {
        setCreateSuccess(false);
        setIsCreating(false);
        setIsOpen(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to create company:', error);
      setIsCreating(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!deleteTarget) return;
    
    try {
      await companyApi.delete(deleteTarget.id);
      // Remove the deleted company from the list
      setCompanies(companies.filter(c => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      console.error('Failed to delete company:', error);
    }
  };

  // Don't show the dropdown for anonymous users
  if (!isAuthenticated) {
    return (
      <FloatingLabelInput
        ref={inputRef}
        name="company"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        label={label}
        placeholder={placeholder}
        isPlaceholder={isPlaceholder}
        onCustomFocus={onCustomFocus}
        onCustomBlur={onCustomBlur}
      />
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <FloatingLabelInput
          ref={inputRef}
          name="company"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          label={label}
          placeholder={placeholder}
          isPlaceholder={isPlaceholder}
          onCustomFocus={onCustomFocus}
          onCustomBlur={onCustomBlur}
          className={`${companyId ? "pr-8 bg-primary-50/20" : ""}`}
        />
        
        {companyId && (
          <button
            type="button"
            onClick={handleClearCompany}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full text-gray-500 hover:text-gray-700 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50"
            title={t('company.clearSelection')}
            aria-label={t('company.clearSelection')}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        
        {!companyId && isLoading && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <svg className="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isOpen && !companyId && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-lg max-h-60 overflow-auto border border-gray-100"
          >
            <div className="py-1">
              {!value.trim() && companies.length > 0 && (
                <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('company.recentCompanies')}
                </div>
              )}
              
              {companies.length > 0 ? (
                companies.map((company) => (
                  <div
                    key={company.id}
                    className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between group"
                  >
                    <div 
                      className="flex-1"
                      onClick={() => handleCompanySelect(company)}
                    >
                      <div className="font-medium">{company.name}</div>
                      <div className="text-xs text-gray-500">
                        {company.address}, {company.postalCode} {company.city}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(company);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                      aria-label={t('actions.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  {value.trim() 
                    ? t('company.noResults') 
                    : isLoading 
                      ? t('common.loading') 
                      : t('company.noRecentCompanies')
                  }
                </div>
              )}

              {value.trim() && !companies.some(c => c.name.toLowerCase() === value.toLowerCase()) && (
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={handleCreateCompany}
                    disabled={isCreating}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between group"
                  >
                    <span className="text-primary-600 font-medium">
                      {t('company.createNew', { name: value })}
                    </span>
                    
                    <div className="w-6 h-6 flex items-center justify-center">
                      {isCreating ? (
                        createSuccess ? (
                          <motion.svg
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 text-green-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </motion.svg>
                        ) : (
                          <svg className="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )
                      ) : (
                        <svg
                          className="w-5 h-5 text-primary-500 group-hover:text-primary-600 transition-colors"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      )}
                    </div>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation dialog */}
      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCompany}
        titleKey="dialog.deleteCompany.title"
        messageKey="dialog.deleteCompany.message"
      />
    </div>
  );
}; 